import { useEffect, useMemo, useState } from 'react'
import { Loader2, Pencil, Trash2, X } from 'lucide-react'
import {
  WorkTypesService,
  type WorkType,
} from '../../services/workTypesService'
import {
  UNCATEGORISED_COLOR,
  type WorkTypeCategory,
} from '../../services/workTypeCategoriesService'
import {
  WORK_TYPE_LEVELS,
  WORK_TYPE_LEVEL_META,
  type WorkTypeLevel,
} from '../../utils/workTypeLevels'
import { WorkTypeLevelBadge } from './WorkTypeLevelBadge'
import { TeamMultiSelect } from './TeamMultiSelect'

interface EditWorkTypeModalProps {
  isOpen: boolean
  workType: WorkType | null
  teamOptions: string[]
  categories: WorkTypeCategory[]
  onClose: () => void
  onChanged: () => Promise<void> | void
}

const UNCATEGORISED_VALUE = ''

export function EditWorkTypeModal({
  isOpen,
  workType,
  teamOptions,
  categories,
  onClose,
  onChanged,
}: EditWorkTypeModalProps) {
  const service = useMemo(() => WorkTypesService.getInstance(), [])
  const [name, setName] = useState('')
  const [teams, setTeams] = useState<string[]>([])
  const [categoryId, setCategoryId] = useState(UNCATEGORISED_VALUE)
  const [level, setLevel] = useState<WorkTypeLevel>(2)
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [error, setError] = useState('')

  const options = useMemo(() => {
    const set = new Set(teamOptions)
    for (const t of workType?.teams || []) set.add(t)
    return [...set]
  }, [teamOptions, workType?.teams])

  useEffect(() => {
    if (!isOpen || !workType) return
    setName(workType.name)
    setTeams([...workType.teams])
    setCategoryId(workType.categoryId || UNCATEGORISED_VALUE)
    setLevel(workType.level)
    setError('')
    setConfirmDelete(false)
  }, [isOpen, workType])

  if (!isOpen || !workType) return null

  const selectedCategory =
    categoryId === UNCATEGORISED_VALUE
      ? null
      : categories.find((c) => c.id === categoryId) || null
  const previewColor = selectedCategory?.color || UNCATEGORISED_COLOR
  const previewCategoryLabel = selectedCategory?.name || 'Uncategorised'

  const handleSave = async () => {
    if (!name.trim()) {
      setError('Work type name is required')
      return
    }
    if (teams.length === 0) {
      setError('Select at least one team')
      return
    }
    try {
      setSaving(true)
      setError('')
      await service.updateWorkType(workType.id, {
        name: name.trim(),
        teams,
        categoryId: categoryId === UNCATEGORISED_VALUE ? null : categoryId,
        level,
      })
      await onChanged()
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update work type')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    try {
      setDeleting(true)
      setError('')
      await service.deleteWorkType(workType.id)
      await onChanged()
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete work type')
    } finally {
      setDeleting(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <Pencil className="w-5 h-5 text-teal-600" />
            <h2 className="text-lg font-semibold text-gray-900">Edit work type</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div>
          )}

          {confirmDelete ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 space-y-3">
              <p className="text-sm text-amber-900">
                Delete <span className="font-semibold">{workType.name}</span>? This can’t be
                undone.
              </p>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setConfirmDelete(false)}
                  disabled={deleting}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-white"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={deleting}
                  className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                >
                  {deleting && <Loader2 className="w-4 h-4 animate-spin" />}
                  Delete
                </button>
              </div>
            </div>
          ) : (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Teams *
                </label>
                <TeamMultiSelect options={options} value={teams} onChange={setTeams} />
              </div>

              <div>
                <label
                  htmlFor="edit-work-type-name"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Name *
                </label>
                <input
                  id="edit-work-type-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  autoFocus
                />
              </div>

              <div>
                <label
                  htmlFor="edit-work-type-category"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Category
                </label>
                <select
                  id="edit-work-type-category"
                  value={categoryId}
                  onChange={(e) => setCategoryId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                >
                  <option value={UNCATEGORISED_VALUE}>Uncategorised</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label
                  htmlFor="edit-work-type-level"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Level
                </label>
                <select
                  id="edit-work-type-level"
                  value={level}
                  onChange={(e) => setLevel(Number(e.target.value) as WorkTypeLevel)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                >
                  {WORK_TYPE_LEVELS.map((l) => (
                    <option key={l} value={l}>
                      {WORK_TYPE_LEVEL_META[l].shortLabel}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Preview</label>
                <div
                  className="relative h-[10.5rem] rounded-xl border border-gray-200 p-4 flex flex-col"
                  style={{
                    borderTopColor: previewColor,
                    borderTopWidth: 3,
                    backgroundColor: `${previewColor}08`,
                  }}
                >
                  <p className="text-sm font-medium text-gray-900 leading-snug line-clamp-5">
                    {name.trim() || 'Work type name'}
                  </p>
                  <div className="mt-auto pt-2 flex items-end justify-between gap-2">
                    <p className="text-xs font-medium truncate" style={{ color: previewColor }}>
                      {previewCategoryLabel}
                    </p>
                    <WorkTypeLevelBadge level={level} />
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {!confirmDelete && (
          <div className="flex items-center justify-between gap-3 p-4 border-t border-gray-200 bg-gray-50">
            <button
              type="button"
              onClick={() => setConfirmDelete(true)}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            >
              <Trash2 className="w-4 h-4" />
              Delete
            </button>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 text-sm"
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="inline-flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 text-sm"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
